import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { territoriesApi, inscriptionsApi, partnersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import InteractiveMap from '../components/InteractiveMap';
import LiveRanking from '../components/LiveRanking';
import PhotoUpload from '../components/PhotoUpload';

// --- THE BULLETPROOF URL GENERATOR ---
// This safely strips out the bad code strings that accidentally got saved to the database.
const buildImgUrl = (dbValue, folderName) => {
  if (!dbValue) return '';
  
  // 1. CLEAN THE BAD DATABASE DATA: Strip out the literal code string if it got saved!
  let cleanName = dbValue.replace("(process.env.REACT_APP_API_BASE || '')+", "");
  cleanName = cleanName.replace("(process.env.REACT_APP_API_BASE || '')", "");
  cleanName = cleanName.replace(/['"]/g, ''); // Remove any stray quotes
  
  // 2. Check if it's already a valid full URL
  if (cleanName.startsWith('http') || cleanName.startsWith('data:') || cleanName.startsWith('blob:')) {
    return cleanName;
  }
  
  // 3. Clean up the string to get just the filename
  if (cleanName.startsWith('/uploads/')) cleanName = cleanName.replace('/uploads/', '');
  if (cleanName.startsWith('uploads/')) cleanName = cleanName.replace('uploads/', '');
  cleanName = cleanName.replace(/^\/+/, ''); // Remove any leftover slashes
  
  // 4. Prevent duplicating the folder name
  if (cleanName.startsWith(`${folderName}/`)) {
    cleanName = cleanName.replace(`${folderName}/`, '');
  }
  
  const baseUrl = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
  
  // Outputs EXACTLY: http://localhost:3001/uploads/logos/partner-123.webp
  return `${baseUrl}/uploads/${folderName}/${cleanName}`;
};

// ========================
// CARTE TAB
// ========================
function MapTab({ territory }) {
  return (
    <div>
      <InteractiveMap territory={territory} />
      <h3 style={{ fontSize: 19, fontWeight: 700, marginBottom: 18 }}>Checkpoints</h3>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {(territory.checkpoints || []).sort((a, b) => a.order - b.order).map((cp, i) => (
          <div key={cp._id || i} style={{
            display: 'grid', gridTemplateColumns: '48px 1fr auto',
            gap: 16, padding: '14px 18px', alignItems: 'center',
            borderBottom: '1px solid rgba(140,122,106,0.08)',
            background: i % 2 === 0 ? '#fff' : 'transparent'
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: cp.type === 'start' ? '#2E7D32' : cp.type === 'finish' ? territory.color : `${territory.color}CC`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontFamily: 'Space Mono, monospace', fontSize: 11, fontWeight: 700
            }}>
              {cp.type === 'start' ? 'S' : cp.type === 'finish' ? 'F' : cp.order}
            </div>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 2 }}>{cp.name}</div>
              <div style={{ fontSize: 11, color: '#8C7A6A', fontFamily: 'Space Mono, monospace' }}>
                {Number(cp.lat).toFixed(4)}°N • {Number(cp.lng).toFixed(4)}°E
              </div>
            </div>
            <span style={{
              padding: '3px 10px', borderRadius: 2, fontSize: 10,
              fontFamily: 'Space Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.08em',
              background: cp.type === 'start' ? '#E8F5E9' : cp.type === 'finish' ? '#FBE9E7' : '#F5F0E8',
              color: cp.type === 'start' ? '#2E7D32' : cp.type === 'finish' ? territory.color : '#8C7A6A'
            }}>
              {cp.type === 'start' ? 'Départ' : cp.type === 'finish' ? 'Arrivée' : 'CP'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========================
// INSCRIPTION TAB
// ========================
function InscriptionTab({ territory }) {
  const [form, setForm] = useState({
    responsableNom: '', responsableEmail: '', responsablePhone: '',
    teamName: '', members: [{ nom: '' }, { nom: '' }],
    formula: 'decouverte', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const addMember = () => setForm(f => ({ ...f, members: [...f.members, { nom: '' }] }));
  const updateMember = (i, val) => setForm(f => ({ ...f, members: f.members.map((m, mi) => mi === i ? { ...m, nom: val } : m) }));
  const removeMember = (i) => { if (form.members.length > 2) setForm(f => ({ ...f, members: f.members.filter((_, mi) => mi !== i) })); };

  const submit = async () => {
    if (!form.responsableNom || !form.responsableEmail || !form.teamName) {
      setError('Remplissez les champs obligatoires'); return;
    }
    const edition = (() => { const d = new Date(territory.nextEvent || Date.now()); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
    setSubmitting(true);
    setError('');
    try {
      await inscriptionsApi.create(territory.id, { ...form, edition, members: form.members.filter(m => m.nom.trim()) });
      setSuccess('Inscription envoyée ! Un email de confirmation vous a été adressé.');
    } catch (err) {
      setError(err.message || "Erreur lors de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return (
    <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
      <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Inscription reçue !</h3>
      <p style={{ color: '#5A4A3C', marginBottom: 24 }}>{success}</p>
      <button onClick={() => { setSuccess(''); setForm({ responsableNom:'', responsableEmail:'', responsablePhone:'', teamName:'', members:[{nom:''},{nom:''}], formula:'decouverte', message:'' }); }}
        style={{ padding: '10px 24px', background: 'none', border: '1.5px solid #1A1208', borderRadius: 2, fontFamily: 'Space Mono, monospace', fontSize: 12, cursor: 'pointer' }}>
        Nouvelle inscription
      </button>
    </div>
  );

  const inputStyle = { width: '100%', padding: '11px 14px', border: '1px solid rgba(140,122,106,0.3)', borderRadius: 2, fontFamily: 'Familjen Grotesk, sans-serif', fontSize: 15, outline: 'none' };
  const labelStyle = { display: 'block', fontFamily: 'Space Mono, monospace', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C7A6A', marginBottom: 6 };

  return (
    <div style={{ maxWidth: 580, margin: '0 auto' }}>
      <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 6 }}>S'inscrire à RADAR {territory.name}</h2>
      <p style={{ color: '#8C7A6A', marginBottom: 36 }}>
        Prochain événement : <strong>{territory.nextEvent ? new Date(territory.nextEvent).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'À venir'}</strong>
      </p>

      <div style={{ marginBottom: 18 }}><label style={labelStyle}>Nom du responsable *</label><input style={inputStyle} value={form.responsableNom} onChange={e => setForm(f => ({ ...f, responsableNom: e.target.value }))} placeholder="Prénom Nom" /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div><label style={labelStyle}>Email *</label><input type="email" style={inputStyle} value={form.responsableEmail} onChange={e => setForm(f => ({ ...f, responsableEmail: e.target.value }))} placeholder="vous@exemple.fr" /></div>
        <div><label style={labelStyle}>Téléphone</label><input style={inputStyle} value={form.responsablePhone} onChange={e => setForm(f => ({ ...f, responsablePhone: e.target.value }))} placeholder="+33 6..." /></div>
      </div>
      <div style={{ marginBottom: 18 }}><label style={labelStyle}>Nom de l'équipe *</label><input style={inputStyle} value={form.teamName} onChange={e => setForm(f => ({ ...f, teamName: e.target.value }))} placeholder="Les Chamois Express..." /></div>

      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Membres de l'équipe (2 minimum)</label>
        {form.members.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input style={{ ...inputStyle, flex: 1 }} value={m.nom} onChange={e => updateMember(i, e.target.value)} placeholder={`Membre ${i + 1}`} />
            {form.members.length > 2 && (
              <button onClick={() => removeMember(i)} style={{ padding: '0 12px', background: 'none', border: '1px solid rgba(140,122,106,0.3)', borderRadius: 2, cursor: 'pointer', color: '#8C7A6A' }}>✕</button>
            )}
          </div>
        ))}
        {form.members.length < 5 && (
          <button onClick={addMember} style={{ fontSize: 13, color: '#E8532A', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Space Mono, monospace', marginTop: 4 }}>+ Ajouter un membre</button>
        )}
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Formule</label>
        <select style={{ ...inputStyle }} value={form.formula} onChange={e => setForm(f => ({ ...f, formula: e.target.value }))}>
          <option value="decouverte">Découverte</option>
          <option value="sport">Sport</option>
          <option value="expert">Expert</option>
          <option value="nocturne">Nocturne</option>
        </select>
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>Message (facultatif)</label>
        <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Questions ou informations particulières..." />
      </div>

      {error && <p style={{ color: '#E8532A', fontSize: 13, marginBottom: 14 }}>{error}</p>}

      <button onClick={submit} disabled={submitting} style={{
        width: '100%', padding: '14px', background: '#E8532A', color: '#fff', border: 'none',
        borderRadius: 2, fontFamily: 'Space Mono, monospace', fontSize: 13, letterSpacing: '0.06em',
        textTransform: 'uppercase', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1
      }}>
        {submitting ? 'Envoi...' : "Envoyer l'inscription →"}
      </button>
      <p style={{ fontSize: 12, color: '#8C7A6A', textAlign: 'center', marginTop: 10 }}>
        Un email de confirmation sera envoyé à {form.responsableEmail || 'votre adresse'}
      </p>
    </div>
  );
}

// ========================
// RÈGLES TAB
// ========================
function ReglesTab({ territory }) {
  const [open, setOpen] = useState(0);
  const rules = territory.rules || [];
  return (
    <div>
      <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 6 }}>Règles — {territory.name}</h2>
      <p style={{ color: '#8C7A6A', marginBottom: 36 }}>Règlement spécifique à cette étape RADAR.</p>
      {rules.length === 0 ? <p style={{ color: '#8C7A6A' }}>Règlement à venir.</p> : (
        <div>
          {rules.sort((a, b) => a.order - b.order).map((rule, i) => (
            <div key={i} style={{ borderBottom: '1px solid rgba(140,122,106,0.15)' }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 4px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: 17, fontWeight: 600 }}>{rule.title}</span>
                <span style={{ fontSize: 20, color: '#8C7A6A', transform: open === i ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>+</span>
              </button>
              {open === i && <div style={{ padding: '0 4px 22px', color: '#5A4A3C', lineHeight: 1.7 }}>{rule.content}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ========================
// GALERIE TAB
// ========================
function GalerieTab({ territory, canManage, onGalleryUpdate }) {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 4 }}>Galerie</h2>
          <p style={{ color: '#8C7A6A' }}>RADAR {territory.name}</p>
        </div>
        {territory.instagramHandle && (
          <a href={`https://instagram.com/${territory.instagramHandle}`} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: '1px solid rgba(140,122,106,0.3)', borderRadius: 2, fontSize: 13, fontFamily: 'Space Mono, monospace', color: '#8C7A6A', textDecoration: 'none' }}>
            📸 @{territory.instagramHandle}
          </a>
        )}
      </div>

      {canManage && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>Ajouter des photos</h3>
          <PhotoUpload territoryId={territory.id} onUploaded={(imgs) => { if (onGalleryUpdate) onGalleryUpdate(imgs); }} />
        </div>
      )}

      {(territory.gallery || []).length === 0 ? (
        <p style={{ color: '#8C7A6A' }}>Aucune photo pour l'instant.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          {territory.gallery.map((img, i) => (
            <div key={img._id || i} onClick={() => setLightbox(img)} style={{ cursor: 'pointer', borderRadius: 4, overflow: 'hidden', aspectRatio: '4/3', background: '#E8EFE4' }}>
              {img.type === 'video' ? (
                <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, background: 'rgba(0,0,0,0.05)' }}>
                  <span style={{ fontSize:28 }}>🎬</span>
                  <p style={{ fontSize:9, color:'#8C7A6A', textAlign:'center', wordBreak:'break-all', fontFamily:'Space Mono,monospace', padding: '0 10px' }}>Vidéo</p>
                </div>
              ) : (
                <img 
                  src={buildImgUrl(img.url, 'territories')}
                  alt={img.alt || ''} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                  onMouseLeave={e => e.currentTarget.style.transform = ''} 
                />
              )}
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(26,18,8,0.92)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, cursor: 'pointer' }}>
          {lightbox.type === 'video' ? (
             <div style={{ color: '#fff', textAlign: 'center' }}>
               <h2>🎬 Lien Vidéo</h2>
               <a href={lightbox.url} target="_blank" rel="noopener noreferrer" style={{ color: territory.color }}>{lightbox.url}</a>
             </div>
          ) : (
            <img src={buildImgUrl(lightbox.url, 'territories')} alt={lightbox.alt || ''} style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 4 }} />
          )}
        </div>
      )}
    </div>
  );
}

// ========================
// MAIN PAGE
// ========================
export default function TerritoireDetailPageV2() {
  const { id } = useParams();
  const { managesTerritoire } = useAuth();
  const [territory, setTerritory] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('carte');

  useEffect(() => {
    territoriesApi.get(id)
      .then(setTerritory)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    partnersApi.listByTerritory(id)
      .then(setPartners)
      .catch(console.error);
  }, [id]);

  const canManage = managesTerritoire(id);

  if (loading) return <div style={{ paddingTop: 120, textAlign: 'center', color: '#8C7A6A' }}>Chargement...</div>;
  if (notFound) return <Navigate to="/territoire" />;
  if (!territory) return null;

  const tabs = [
    { id: 'carte', label: 'Carte' },
    { id: 'classement', label: 'Classement' },
    { id: 'inscription', label: 'Inscription' },
    { id: 'regles', label: 'Règles' },
    { id: 'galerie', label: 'Galerie' }
  ];

  return (
    <div style={{ paddingTop: 'var(--nav-h, 64px)' }}>
      
      {/* ========================
          ACCUEIL / HEADER SECTION
          ======================== */}
      {/* Background Positioning Layer */}
      <div style={{ position: 'relative', overflow: 'hidden', padding: '40px var(--px,clamp(16px,5vw,40px)) 0', borderBottom: '1px solid rgba(140,122,106,0.15)' }}>
        
        {/* Background Image Layer using standard HTML img */}
        {territory.heroImageUrl ? (
          <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
            <img 
              src={buildImgUrl(territory.heroImageUrl, 'territories')} 
              alt="Background"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            />
            {/* Dark overlay to make text readable */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(26,18,8,0.65), rgba(26,18,8,0.98))' }} />
          </div>
        ) : (
          <div style={{ position: 'absolute', inset: 0, zIndex: 0, background: 'linear-gradient(160deg, #1A1208 0%, #0E0A04 100%)' }} />
        )}

        {/* Content Layer */}
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          
          <div style={{ marginBottom: 24 }}>
            <Link to="/territoire" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontFamily: 'Space Mono, monospace', textDecoration: 'none' }}>
              ← Retour aux territoires
            </Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 20 }}>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              
              {/* AVATAR DISPLAY */}
              {territory.avatarUrl ? (
                <div style={{ width: 90, height: 90, borderRadius: '50%', overflow: 'hidden', border: `3px solid ${territory.color}`, background: '#fff' }}>
                  <img 
                    src={buildImgUrl(territory.avatarUrl, 'avatars')} 
                    alt={territory.name} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                </div>
              ) : (
                <div style={{ width: 90, height: 90, borderRadius: '50%', background: territory.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 36, fontWeight: 700 }}>
                  {territory.name.charAt(0)}
                </div>
              )}

              {/* TITLE & INFO */}
              <div>
                <h1 style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4, color: '#fff' }}>
                  RADAR {territory.name}
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Space Mono, monospace', fontSize: 13 }}>
                  {territory.region} • {territory.players || 0} participants
                  {territory.nextEvent && ` • Prochain : ${new Date(territory.nextEvent).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                </p>
              </div>
            </div>

            {canManage && (
              <Link to="/dashboard" style={{ padding: '10px 18px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, fontFamily: 'Space Mono, monospace', fontSize: 11, color: '#fff', display: 'inline-block', textDecoration: 'none' }}>
                ✏️ Gérer ce territoire
              </Link>
            )}
          </div>

          {/* PARTNERS LOGO DISPLAY */}
          {partners.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 36, flexWrap: 'wrap', padding: '18px 24px', background: 'rgba(255,255,255,0.04)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
              <span style={{ fontSize: 11, fontFamily: 'Space Mono, monospace', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em' }}>
                Partenaires
              </span>
              <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
                {partners.map(p => (
                  <a key={p._id} href={p.website || '#'} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', opacity: 0.8, transition: 'opacity 0.2s', textDecoration: 'none' }} onMouseEnter={e=>e.currentTarget.style.opacity='1'} onMouseLeave={e=>e.currentTarget.style.opacity='0.8'}>
                    {p.logoUrl ? (
                      <img 
                        src={buildImgUrl(p.logoUrl, 'logos')} 
                        alt={p.name} 
                        style={{ maxHeight: 40, maxWidth: 130, objectFit: 'contain' }} 
                      />
                    ) : (
                      <span style={{ fontSize: 14, color: '#fff', fontFamily: 'Space Mono, monospace', fontWeight: 700 }}>{p.name}</span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* TABS MENU */}
          <div style={{ display: 'flex', overflowX: 'auto', gap: 8 }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'Space Mono, monospace', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: activeTab === tab.id ? territory.color : 'rgba(255,255,255,0.5)',
                borderBottom: activeTab === tab.id ? `3px solid ${territory.color}` : '3px solid transparent',
                transition: 'all 0.2s', whiteSpace: 'nowrap'
              }}>{tab.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ========================
          TAB CONTENT
          ======================== */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(24px,5vw,48px) var(--px,clamp(16px,5vw,40px))' }}>
        {activeTab === 'carte' && <MapTab territory={territory} />}
        {activeTab === 'classement' && <LiveRanking territory={territory} />}
        {activeTab === 'inscription' && <InscriptionTab territory={territory} />}
        {activeTab === 'regles' && <ReglesTab territory={territory} />}
        {activeTab === 'galerie' && (
          <GalerieTab
            territory={territory}
            canManage={canManage}
            onGalleryUpdate={(newImgs) => setTerritory(t => ({ ...t, gallery: [...(t.gallery || []), ...newImgs] }))}
          />
        )}
      </div>
    </div>
  );
}